import React, { useState, useEffect } from 'react';
import {
    Alert,
    Snackbar,
    CircularProgress,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { 
    Business as BusinessIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    Security as SecurityIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    AccountTree as DepartmentIcon
} from '@mui/icons-material';
import { API_BASE } from '../utils/apiUtils';
import { checkAdminStatus, getUserData } from '../utils/authUtils';

const CreateDepartment = () => {
    const [departmentData, setDepartmentData] = useState({
        code: '',
        name: '',
        description: '',
        active: true,
        organizationId: null
    });
    
    const [myDepartments, setMyDepartments] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const [organizationsLoading, setOrganizationsLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [errors, setErrors] = useState({});

    // Check if user is super admin and load organizations if needed
    useEffect(() => {
        const userData = getUserData();
        const { isSuperAdmin: isSA } = checkAdminStatus(userData);
        setIsSuperAdmin(isSA);
        
        if (isSA) {
            loadOrganizations();
        } else {
            loadMyDepartments();
        }
    }, []);

    // Load departments when organization changes (for super admin)
    useEffect(() => {
        if (isSuperAdmin && departmentData.organizationId) {
            loadDepartmentsForOrganization(departmentData.organizationId);
        } else if (!isSuperAdmin) {
            loadMyDepartments();
        }
    }, [departmentData.organizationId, isSuperAdmin]);

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const loadOrganizations = async () => {
        setOrganizationsLoading(true);
        try {
            const response = await fetch(`${API_BASE}/admin/organizations/get-all`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setOrganizations(Array.isArray(data) ? data : []);
                
                // Set default organization to user's organization if available
                const userData = getUserData();
                if (userData?.organization?.id) {
                    setDepartmentData(prev => ({ ...prev, organizationId: userData.organization.id }));
                }
            } else {
                throw new Error('Failed to load organizations');
            }
        } catch (error) {
            console.error('Error loading organizations:', error);
            showNotification('Failed to load organizations', 'warning');
            setOrganizations([]);
        } finally {
            setOrganizationsLoading(false);
        }
    };

    const loadDepartmentsForOrganization = async (organizationId) => {
        if (!organizationId) {
            setMyDepartments([]);
            return;
        }
        
        setDepartmentsLoading(true);
        try {
            // Use the admin departments endpoint for super admin
            const response = await fetch(`${API_BASE}/admin/departments/by-organization/${organizationId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                const departments = data.departments || data || [];
                setMyDepartments(Array.isArray(departments) ? departments : []);
            } else {
                throw new Error('Failed to load departments');
            }
        } catch (error) {
            console.error('Error loading departments:', error);
            showNotification('Failed to load departments', 'warning');
            setMyDepartments([]);
        } finally {
            setDepartmentsLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (isSuperAdmin && !departmentData.organizationId) {
            newErrors.organizationId = 'Please select an organization';
        }

        if (!departmentData.code.trim()) {
            newErrors.code = 'Department code is required';
        } else if (!/^[A-Z0-9_-]+$/.test(departmentData.code)) {
            newErrors.code = 'Code must contain only uppercase letters, numbers, underscores, and hyphens';
        }

        if (!departmentData.name.trim()) {
            newErrors.name = 'Department name is required';
        } else if (departmentData.name.trim().length < 3) {
            newErrors.name = 'Department name must be at least 3 characters';
        } else if (departmentData.name.trim().length > 100) {
            newErrors.name = 'Department name cannot exceed 100 characters';
        }

        if (!departmentData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        // Check for duplicate codes within the selected organization
        const targetOrgId = departmentData.organizationId || (getUserData()?.organization?.id);
        if (myDepartments.some(dept => 
            dept.code === departmentData.code.trim().toUpperCase() && 
            dept.organizationId === targetOrgId
        )) {
            newErrors.code = 'This department code already exists in the selected organization';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const loadMyDepartments = async () => {
        setDepartmentsLoading(true);
        try {
            // Get user data to find organization ID
            const userData = localStorage.getItem('userData');
            if (!userData) {
                throw new Error('No user data found');
            }
            
            const parsedUser = JSON.parse(userData);
            const organizationId = parsedUser.organization?.id;
            
            if (!organizationId) {
                throw new Error('No organization ID found in user data');
            }

            const response = await fetch(`${API_BASE}/admin/departments/my-organization`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                const departments = data.departments || data || [];
                setMyDepartments(Array.isArray(departments) ? departments : []);
            } else {
                throw new Error('Failed to load departments');
            }
        } catch (error) {
            console.error('Error loading departments:', error);
            showNotification('Failed to load existing departments', 'warning');
            setMyDepartments([]);
        } finally {
            setDepartmentsLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        if (field === 'code') {
            // Auto-format code to uppercase
            value = value.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
        }
        
        if (field === 'organizationId') {
            // When organization changes, reset departments list
            setMyDepartments([]);
        }
        
        setDepartmentData(prev => ({ ...prev, [field]: value }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            showNotification('Please fix the errors below', 'error');
            return;
        }

        setLoading(true);
        try {
            // Include organizationId if super admin selected one, otherwise backend uses user's org
            const payload = {
                code: departmentData.code,
                name: departmentData.name,
                description: departmentData.description,
                active: departmentData.active
            };
            
            // Add organizationId if super admin selected one
            if (isSuperAdmin && departmentData.organizationId) {
                payload.organizationId = departmentData.organizationId;
            }

            const response = await fetch(`${API_BASE}/admin/departments/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                showNotification('Department created successfully!', 'success');

                // Reset form
                const resetData = {
                    code: '',
                    name: '',
                    description: '',
                    active: true,
                    organizationId: isSuperAdmin ? departmentData.organizationId : null
                };
                setDepartmentData(resetData);
                setErrors({});

                // Reload departments list
                if (isSuperAdmin && departmentData.organizationId) {
                    await loadDepartmentsForOrganization(departmentData.organizationId);
                } else {
                    await loadMyDepartments();
                }
            } else {
                // Try to parse error response as JSON
                let errorMessage = 'Failed to create department';
                try {
                    const errorData = await response.json();
                    if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (errorData.errors && Array.isArray(errorData.errors)) {
                        errorMessage = errorData.errors.join(', ');
                    }
                    
                    // Set field-specific errors if available
                    if (errorData.errors && Array.isArray(errorData.errors)) {
                        const fieldErrors = {};
                        errorData.errors.forEach(err => {
                            // Try to extract field name from error message
                            if (err.includes('name')) {
                                fieldErrors.name = err;
                            } else if (err.includes('code')) {
                                fieldErrors.code = err;
                            } else if (err.includes('description')) {
                                fieldErrors.description = err;
                            }
                        });
                        if (Object.keys(fieldErrors).length > 0) {
                            setErrors(fieldErrors);
                        }
                    }
                } catch (parseError) {
                    // If JSON parsing fails, try text
                    try {
                        const errorText = await response.text();
                        errorMessage = errorText || errorMessage;
                    } catch (textError) {
                        console.error('Error parsing error response:', textError);
                    }
                }
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error('Error creating department:', error);
            showNotification(error.message || 'Failed to create department', 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        const userData = getUserData();
        const defaultOrgId = isSuperAdmin && userData?.organization?.id ? userData.organization.id : null;
        setDepartmentData({
            code: '',
            name: '',
            description: '',
            active: true,
            organizationId: defaultOrgId
        });
        setErrors({});
    };

    return (
        <div className="form-container">
            <div className="form-header">
                <div className="form-icon">
                    <DepartmentIcon />
                </div>
                <h1 className="form-title">Create Department</h1>
                <p className="form-subtitle">
                    Add a new department to organize users and manage access permissions
                </p>
            </div>

                <form onSubmit={handleSubmit}>
                {/* Department Information Section */}
                <div className="form-section">
                    <h2 className="form-section-title">
                        <DepartmentIcon className="form-section-icon" />
                        Department Information
                    </h2>
                    
                    {/* Organization Selector for Super Admin */}
                    {isSuperAdmin && (
                        <div className="form-group">
                            <label className="form-label form-label-required">Organization</label>
                            {organizationsLoading ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CircularProgress size={16} />
                                    <span className="text-sm text-secondary">Loading organizations...</span>
                                </div>
                            ) : (
                                <select
                                    className={`form-input ${errors.organizationId ? 'form-input-error' : ''}`}
                                    value={departmentData.organizationId || ''}
                                    onChange={(e) => handleInputChange('organizationId', e.target.value ? Number(e.target.value) : null)}
                                >
                                    <option value="">Select an organization</option>
                                    {organizations.map((org) => (
                                        <option key={org.id} value={org.id}>
                                            {org.name} {org.active ? '' : '(Inactive)'}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {errors.organizationId && <span className="form-error">{errors.organizationId}</span>}
                            <span className="form-help">Select the organization for this department</span>
                        </div>
                    )}
                    
                    <div className="form-group-row">
                        <div className="form-group">
                            <label className="form-label form-label-required">Department Code</label>
                            <input
                                type="text"
                                className={`form-input ${errors.code ? 'form-input-error' : ''}`}
                                placeholder="DEPT_CODE"
                                value={departmentData.code}
                                onChange={(e) => handleInputChange('code', e.target.value)}
                                maxLength={20}
                            />
                            {errors.code && <span className="form-error">{errors.code}</span>}
                            <span className="form-help">Unique identifier (uppercase letters, numbers, underscore, hyphen)</span>
                        </div>

                        <div className="form-group">
                            <label className="form-label form-label-required">Department Name</label>
                            <input
                                type="text"
                                className={`form-input ${errors.name ? 'form-input-error' : ''}`}
                                placeholder="Department Name"
                                value={departmentData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                            />
                            {errors.name && <span className="form-error">{errors.name}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label form-label-required">Description</label>
                        <textarea
                            className={`form-input form-textarea ${errors.description ? 'form-input-error' : ''}`}
                            placeholder="Describe the department's role and responsibilities"
                                value={departmentData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            rows={4}
                        />
                        {errors.description && <span className="form-error">{errors.description}</span>}
                    </div>

                    <div className="form-group">
                        <label className="form-checkbox">
                            <input
                                type="checkbox"
                                checked={departmentData.active}
                                onChange={(e) => handleInputChange('active', e.target.checked)}
                            />
                            Department is active
                        </label>
                        <span className="form-help">Inactive departments cannot be assigned to users</span>
                    </div>
                </div>

                {/* Existing Departments Section */}
                {myDepartments.length > 0 && (
                    <div className="form-section">
                        <h2 className="form-section-title">
                            <BusinessIcon className="form-section-icon" />
                            Existing Departments
                        </h2>
                        
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {departmentsLoading ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CircularProgress size={16} />
                                    <span className="text-sm text-secondary">Loading departments...</span>
                                </div>
                            ) : (
                                myDepartments.map((dept) => (
                                    <Chip
                                        key={dept.id}
                                        label={`${dept.code} - ${dept.name}`}
                                    variant="outlined"
                                        color={dept.active ? "primary" : "default"}
                                        size="small"
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className="form-actions">
                    <button
                        type="button"
                        className="form-button form-button-secondary"
                        onClick={resetForm}
                        disabled={loading}
                    >
                        <RefreshIcon style={{ width: '16px', height: '16px' }} />
                        Reset Form
                    </button>
                    
                    <button
                        type="submit"
                        className="form-button form-button-primary"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={16} color="inherit" />
                                Creating...
                            </>
                        ) : (
                            <>
                                <AddIcon style={{ width: '16px', height: '16px' }} />
                                Create Department
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Notifications */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
            >
                <Alert 
                    severity={notification.severity}
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                    icon={notification.severity === 'success' ? <CheckIcon /> : <ErrorIcon />}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default CreateDepartment;